// src/Context/AuthContext.jsx

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef
} from "react"
import { supabase } from "../lib/supabaseClient"  // adjust the path as needed
import { ensureUserAccessForAuthenticatedUser } from "../lib/userAccessOrg"
import { assertGoogleOauthAllowed } from "../lib/orgAuthPolicy"
import { storeAuthReturnUrl } from "../lib/authRedirect"

const AuthContext = createContext()

export { AuthContext }

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null)
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [userStatus, setUserStatus] = useState(null)
  const lastUserIdRef = useRef(null)
  // De-duplicates ensureUserAccessAndStatus: React StrictMode (dev) double-mounts this
  // effect, and supabase-js re-emits SIGNED_IN on every tab focus, so the same user would
  // otherwise trigger several identical org-resolve/claim/insert sequences per second.
  const ensureInFlightRef = useRef(new Map())
  const ensureLastRunRef = useRef({ userId: null, at: 0 })
  const ENSURE_MIN_INTERVAL_MS = 30_000
  const debugAuth = import.meta?.env?.DEV && localStorage.getItem('auth_debug') === '1'

  useEffect(() => {
    const ensureUserAccessAndStatus = (authUser) => {
      if (!authUser) {
        ensureLastRunRef.current = { userId: null, at: 0 }
        return runEnsureUserAccessAndStatus(null)
      }
      const inFlight = ensureInFlightRef.current.get(authUser.id)
      if (inFlight) return inFlight
      const last = ensureLastRunRef.current
      if (last.userId === authUser.id && Date.now() - last.at < ENSURE_MIN_INTERVAL_MS) {
        return Promise.resolve()
      }
      const promise = runEnsureUserAccessAndStatus(authUser).finally(() => {
        ensureInFlightRef.current.delete(authUser.id)
      })
      ensureInFlightRef.current.set(authUser.id, promise)
      return promise
    }

    const runEnsureUserAccessAndStatus = async (authUser) => {
      if (!authUser) {
        setUserStatus(null)
        if (debugAuth) console.log('[AuthContext] No user, userStatus set to null')
        return
      }
      try {
        const targetOrigin =
          window.location.pathname === '/auth/callback'
            ? new URLSearchParams(window.location.search).get('target')
            : null

        const ensured = await ensureUserAccessForAuthenticatedUser({
          supabaseClient: supabase,
          authUser,
          hostname: window.location.hostname,
          targetOrigin,
          debug: debugAuth,
        })

        if (ensured.source === 'no_session') {
          // No live token yet: don't query as anon or overwrite the known status.
          // The next auth event (SIGNED_IN / TOKEN_REFRESHED) will retry.
          return
        }
        ensureLastRunRef.current = { userId: authUser.id, at: Date.now() }

        if (debugAuth && ensured.didWrite) {
          console.log('[AuthContext] Ensured user_access organization:', ensured.organization, 'via', ensured.source)
        }
      } catch (e) {
        if (debugAuth) console.warn('[AuthContext] org auto-assign skipped:', e?.message || e)
      }
      // Fetch user status from user_access
      const { data } = await supabase
        .from('user_access')
        .select('status')
        .eq('id', authUser.id)
        .maybeSingle()
      setUserStatus(data?.status || 'active')
      if (debugAuth) console.log('[AuthContext] userStatus set:', data?.status || 'active')
    }

    // 1) Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (debugAuth) console.log('[AuthContext] Initial session:', session)
      setSession(session)
      const nextUser = session?.user ?? null
      setUser(prev => (prev?.id && nextUser?.id && prev.id === nextUser.id ? prev : nextUser))
      setLoading(false)
      lastUserIdRef.current = session?.user?.id || null
      ensureUserAccessAndStatus(session?.user ?? null)
    })

    // 2) Subscribe to future auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, newSession) => {
        if (debugAuth && _event !== 'TOKEN_REFRESHED') {
          console.log('[AuthContext] Auth state changed:', _event, newSession)
        }
        setSession(newSession)
        const nextUser = newSession?.user ?? null
        setUser(prev => (prev?.id && nextUser?.id && prev.id === nextUser.id ? prev : nextUser))

        const nextUserId = nextUser?.id || null
        const sameUser = lastUserIdRef.current && nextUserId && lastUserIdRef.current === nextUserId
        lastUserIdRef.current = nextUserId

        if (_event === 'TOKEN_REFRESHED' && sameUser) return
        ensureUserAccessAndStatus(nextUser)
      }
    )

    // 3) Cleanup on unmount
    return () => {
      subscription.unsubscribe()
    }
  }, [])

  useEffect(() => {
    if (!debugAuth) return
    console.log('[AuthContext] user state changed:', user)
    console.log('[AuthContext] loading state:', loading)
  }, [user, loading]);

  // === Auth actions ===

  const signUp = async (email, password, options = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      ...(Object.keys(options || {}).length ? { options } : {})
    })
    if (error) throw error
    return data
  }

  const login = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    if (error) throw error
    // Immediately reflect new session/user in state
    setSession(data.session)
    setUser(data.user)
    return data
  }

  const refreshAuthSession = async () => {
    const { data, error } = await supabase.auth.getSession()
    if (error) throw error
    const nextSession = data?.session ?? null
    const nextUser = nextSession?.user ?? null
    setSession(nextSession)
    setUser(nextUser)
    lastUserIdRef.current = nextUser?.id || null
    return nextSession
  }

  const loginWithProvider = async (provider, { targetOrigin, returnUrl } = {}) => {
    if (String(provider || '').trim().toLowerCase() === 'google') {
      await assertGoogleOauthAllowed({
        hostname: window.location.hostname,
        targetOrigin,
      })
    }

    // Stash before navigating away to the provider so AuthCallback can
    // restore the deep link once the session comes back (same-origin
    // sessionStorage survives the OAuth round trip in the same tab).
    storeAuthReturnUrl(returnUrl)

    const currentOrigin = window.location.origin
    const envAuthHost = (import.meta.env.VITE_AUTH_HOST || '').trim()
    const FALLBACK_AUTH_HOST = 'https://learn.astra-xr.com'
    const MAX_RETRIES = 2
    const RETRY_DELAY = 1000 // 1 second base delay
    
    const isDevBuild = import.meta.env?.DEV ?? false
    const isLocalHostname = currentOrigin.includes('localhost') || currentOrigin.includes('127.0.0.1')
    const preferLocalAuth = isDevBuild || isLocalHostname
    const resolvedAuthHost = preferLocalAuth
      ? currentOrigin
      : (envAuthHost || FALLBACK_AUTH_HOST)
    const desiredTargetOrigin = targetOrigin || currentOrigin
    const callbackTarget = desiredTargetOrigin && desiredTargetOrigin !== resolvedAuthHost
      ? desiredTargetOrigin
      : null
    const needsCrossDomainBounce = !preferLocalAuth && resolvedAuthHost !== currentOrigin
    const redirectUrl = callbackTarget
      ? `${resolvedAuthHost}/auth/callback?target=${encodeURIComponent(callbackTarget)}`
      : `${resolvedAuthHost}/auth/callback`
    
    console.log('[Auth] OAuth redirect configuration:', {
      provider,
      currentOrigin,
      resolvedAuthHost,
      preferLocalAuth,
      needsCrossDomainBounce,
      redirectUrl,
      callbackTarget
    })
    
    if (needsCrossDomainBounce) {
      const bounceUrl = new URL(`${resolvedAuthHost}/login`)
      bounceUrl.searchParams.set('provider', provider)
      bounceUrl.searchParams.set('target', callbackTarget || desiredTargetOrigin)
      window.location.href = bounceUrl.toString()
      return
    }
    
    const attemptSignIn = async (retryCount = 0) => {
      try {
        const { error } = await supabase.auth.signInWithOAuth({
          provider,
          options: {
            redirectTo: redirectUrl,
            queryParams: { prompt: 'select_account' }
          },
        })
        
        if (error) {
          const isNetworkError = error.message?.includes('fetch') || 
                                error.message?.includes('network') ||
                                error.message?.includes('timeout') ||
                                error.status === 0
          
          if (isNetworkError && retryCount < MAX_RETRIES) {
            const delay = RETRY_DELAY * Math.pow(2, retryCount)
            console.warn(`[Auth] ${provider} OAuth failed (attempt ${retryCount + 1}/${MAX_RETRIES + 1}), retrying in ${delay}ms...`)
            await new Promise(resolve => setTimeout(resolve, delay))
            return attemptSignIn(retryCount + 1)
          }
          
          throw error
        }
      } catch (err) {
        const isNetworkError = err.message?.includes('fetch') || 
                              err.message?.includes('network') ||
                              err.message?.includes('timeout') ||
                              err.status === 0
        if (isNetworkError && retryCount < MAX_RETRIES) {
          const delay = RETRY_DELAY * Math.pow(2, retryCount)
          console.warn(`[Auth] ${provider} OAuth error (attempt ${retryCount + 1}/${MAX_RETRIES + 1}), retrying in ${delay}ms...`)
          await new Promise(resolve => setTimeout(resolve, delay))
          return attemptSignIn(retryCount + 1)
        }
        console.error(`[Auth] ${provider} OAuth error:`, err)
        throw err
      }
    }
    
    return attemptSignIn(0)
  }

  const loginWithSSO = async ({
    domain,
    providerId,
    targetOrigin,
    returnUrl,
    authMode = 'supabase_saml',
    brokerProvider = 'workos',
    brokerOrgId,
    brokerConnectionId
  } = {}) => {
    if (authMode === 'broker') {
      if (!brokerOrgId && !brokerConnectionId) {
        throw new Error('Provide brokerOrgId or brokerConnectionId for broker SSO login')
      }
    } else if (!domain && !providerId) {
      throw new Error('Provide domain or providerId for SSO login')
    }

    storeAuthReturnUrl(returnUrl)

    const currentOrigin = window.location.origin
    const envAuthHost = (import.meta.env.VITE_AUTH_HOST || '').trim()
    const FALLBACK_AUTH_HOST = 'https://learn.astra-xr.com'

    const isDevBuild = import.meta.env?.DEV ?? false
    const isLocalHostname = currentOrigin.includes('localhost') || currentOrigin.includes('127.0.0.1')
    const preferLocalAuth = isDevBuild || isLocalHostname
    const resolvedAuthHost = preferLocalAuth
      ? currentOrigin
      : (envAuthHost || FALLBACK_AUTH_HOST)
    const desiredTargetOrigin = targetOrigin || currentOrigin
    const callbackTarget = desiredTargetOrigin && desiredTargetOrigin !== resolvedAuthHost
      ? desiredTargetOrigin
      : null
    const needsCrossDomainBounce = !preferLocalAuth && resolvedAuthHost !== currentOrigin
    const redirectUrl = callbackTarget
      ? `${resolvedAuthHost}/auth/callback?target=${encodeURIComponent(callbackTarget)}`
      : `${resolvedAuthHost}/auth/callback`

    if (needsCrossDomainBounce) {
      const bounceUrl = new URL(`${resolvedAuthHost}/login`)
      bounceUrl.searchParams.set('sso_mode', authMode)
      if (domain) bounceUrl.searchParams.set('sso_domain', domain)
      if (providerId) bounceUrl.searchParams.set('sso_provider_id', providerId)
      if (authMode === 'broker') {
        if (brokerProvider) bounceUrl.searchParams.set('sso_broker_provider', brokerProvider)
        if (brokerOrgId) bounceUrl.searchParams.set('sso_broker_org_id', brokerOrgId)
        if (brokerConnectionId) bounceUrl.searchParams.set('sso_broker_connection_id', brokerConnectionId)
      }
      bounceUrl.searchParams.set('target', callbackTarget || desiredTargetOrigin)
      window.location.href = bounceUrl.toString()
      return
    }

    let error = null
    if (authMode === 'broker') {
      if (brokerProvider !== 'workos') {
        throw new Error(`Unsupported broker provider for Supabase handoff: ${brokerProvider}`)
      }
      const queryParams = {}
      if (brokerOrgId) queryParams.organization = brokerOrgId
      if (brokerConnectionId) queryParams.connection = brokerConnectionId
      const res = await supabase.auth.signInWithOAuth({
        provider: 'workos',
        options: {
          redirectTo: redirectUrl,
          ...(Object.keys(queryParams).length ? { queryParams } : {})
        }
      })
      error = res.error
    } else {
      const payload = providerId
        ? { providerId, options: { redirectTo: redirectUrl } }
        : { domain, options: { redirectTo: redirectUrl } }
      const res = await supabase.auth.signInWithSSO(payload)
      error = res.error
    }
    if (error) throw error
  }

  const logout = async () => {
    const { error } = await supabase.auth.signOut()
    if (error) throw error
    setSession(null)
    setUser(null)
  }

  return (
    <AuthContext.Provider
      value={{ session, user, userStatus, loading, signUp, login, logout, loginWithProvider, loginWithSSO, refreshAuthSession }}
    >
      {loading ? (
        <div className="min-h-screen flex items-center justify-center text-lg">Loading...</div>
      ) : (
        children
      )}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error("useAuth must be used within <AuthProvider>")
  }
  return ctx
}
